import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["className", "showVerticalOnHover", "fadeTop", "fadeBottom", "fadeTopHeight", "fadeBottomHeight", "children"];
import React, { memo, useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { Icon, createUseStyles } from 'hoi-poi-ui';
import classnames from 'classnames';
import ScrollBar from "./ScrollBar";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'QuickFilters');
var _ref2 = /*#__PURE__*/React.createElement("div", null);
var _ref3 = /*#__PURE__*/React.createElement(Icon, {
  name: "chevronRight"
});
var _ref4 = /*#__PURE__*/React.createElement(Icon, {
  name: "chevronLeft"
});
const ScrollBarsCustom = /*#__PURE__*/memo(_ref => {
  let {
      className,
      showVerticalOnHover,
      fadeTop,
      fadeBottom,
      fadeTopHeight,
      fadeBottomHeight,
      children
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const classes = useStyles();
  const scrollBarsRef = useRef(null);
  const innerRef = useRef(null);
  const [scrolledToLeft, setScrolledToLeft] = useState(true);
  const [scrolledToRight, setScrolledToRight] = useState(false);
  const timeoutRef = useRef(null);
  const timeoutAutoCancelRef = useRef(null);
  const handleMouse = useCallback(e => {
    var _childNodes$, _childNodes$$classLis, _childNodes$2, _childNodes$2$classLi;
    e.stopPropagation();
    const childNodes = e.target.lastChild.childNodes;
    if (!childNodes.length) return;
    (_childNodes$ = childNodes[1]) === null || _childNodes$ === void 0 ? void 0 : (_childNodes$$classLis = _childNodes$.classList) === null || _childNodes$$classLis === void 0 ? void 0 : _childNodes$$classLis.toggle(classes.shown);
    (_childNodes$2 = childNodes[2]) === null || _childNodes$2 === void 0 ? void 0 : (_childNodes$2$classLi = _childNodes$2.classList) === null || _childNodes$2$classLi === void 0 ? void 0 : _childNodes$2$classLi.toggle(classes.shown);
  }, [classes]);
  const scrollTo = useCallback(distance => {
    var _innerRef$current, _innerRef$current2;
    const currentScrollDelta = (_innerRef$current = innerRef.current) === null || _innerRef$current === void 0 ? void 0 : _innerRef$current.getScrollLeft();
    (_innerRef$current2 = innerRef.current) === null || _innerRef$current2 === void 0 ? void 0 : _innerRef$current2.scrollLeft(currentScrollDelta + distance);
    return true;
  }, []);
  const scrollToEdge = useCallback(edge => {
    clearInterval(timeoutRef.current);
    clearInterval(timeoutAutoCancelRef.current);
    timeoutAutoCancelRef.current = setInterval(function () {
      var _innerRef$current3, _innerRef$current4;
      const lastScroll = (_innerRef$current3 = innerRef.current) === null || _innerRef$current3 === void 0 ? void 0 : _innerRef$current3.getScrollLeft();
      scrollTo(40 * (edge > 0 ? 1 : -1));
      if (lastScroll === ((_innerRef$current4 = innerRef.current) === null || _innerRef$current4 === void 0 ? void 0 : _innerRef$current4.getScrollLeft())) clearInterval(timeoutAutoCancelRef.current);
    }, 10);
  }, [scrollTo]);
  const scrollOnOver = useCallback(function (distance, time) {
    if (time === void 0) {
      time = 50;
    }
    clearInterval(timeoutRef.current);
    timeoutRef.current = setInterval(function () {
      scrollTo(distance);
    }, time);
    return true;
  }, [scrollTo]);
  const onMouseOut = useCallback(() => {
    clearInterval(timeoutRef.current);
    return true;
  }, []);
  useEffect(() => {
    if (scrollBarsRef.current) {
      scrollBarsRef.current.addEventListener('mouseenter', handleMouse);
      scrollBarsRef.current.addEventListener('mouseleave', handleMouse);
    }
  }, [handleMouse]);
  const renderFadeTop = useMemo(() => {
    if (!fadeTop) return null;
    let style = {};
    if (fadeTopHeight) style['height'] = fadeTopHeight;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.topFade,
      style: style
    });
  }, [classes, fadeTop, fadeTopHeight]);
  const renderFadeBottom = useMemo(() => {
    if (!fadeBottom) return null;
    let style = {};
    if (fadeBottomHeight) style['height'] = fadeBottomHeight;
    return /*#__PURE__*/React.createElement("div", {
      className: classes.bottomFade,
      style: style
    });
  }, [classes, fadeBottom, fadeBottomHeight]);
  const renderTrackHorizontal = useCallback(() => {
    return _ref2;
  }, []);
  const renderThumbHorizontal = useCallback(() => {
    const rightScroll = scrolledToRight ? null : /*#__PURE__*/React.createElement("div", {
      className: [classes.scrollButton, classes.buttonRight].join(' '),
      onClick: () => scrollToEdge(1),
      onMouseOver: () => scrollOnOver(10),
      onMouseOut: onMouseOut
    }, _ref3);
    const leftScroll = scrolledToLeft ? null : /*#__PURE__*/React.createElement("div", {
      className: [classes.scrollButton, classes.buttonLeft].join(' '),
      onClick: () => scrollToEdge(-1),
      onMouseOver: () => scrollOnOver(-10),
      onMouseOut: onMouseOut
    }, _ref4);
    return /*#__PURE__*/React.createElement("div", {
      className: classes.scrollContainer
    }, rightScroll, leftScroll);
  }, [scrolledToLeft, scrolledToRight, scrollToEdge, scrollOnOver, onMouseOut, classes]);
  const handleScrollUpdate = useCallback(values => {
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = values;
    const roundedScrolledToLeft = Math.ceil(scrollLeft);
    setScrolledToLeft(roundedScrolledToLeft === 0);
    setScrolledToRight(roundedScrolledToLeft === scrollWidth - clientWidth);
  }, []);
  const rootClassName = classnames(classes.root, {
    [classes.withFade]: fadeTop || fadeBottom
  });
  let classNamesScrollbars = classnames('', {
    [classes.showVerticalOnHover]: showVerticalOnHover
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: scrollBarsRef,
    className: rootClassName
  }, /*#__PURE__*/React.createElement(ScrollBar, _extends({}, props, {
    autoHide: false,
    hideTracksWhenNotNeeded: true,
    className: classNamesScrollbars,
    renderTrackHorizontal: renderTrackHorizontal,
    renderThumbHorizontal: renderThumbHorizontal,
    onUpdate: handleScrollUpdate,
    ref: innerRef
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.childrenRoot
  }, children)), renderFadeTop, renderFadeBottom);
});
export default ScrollBarsCustom;